import { MaybeRefOrGetter } from '@vue/reactivity'
import { computed, toValue } from 'vue'

import { useSettingStore } from '@/stores/setting'

export type DateRange = (Date | null)[] | undefined
export type DateRangeISO = (string | null)[] | undefined

function toISODate(date: Date): string {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
    .getDate()
    .toString()
    .padStart(2, '0')}`
}
function fromISODate(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function toISODateRange(dateRange: DateRange): DateRangeISO {
  if (!dateRange) return
  return [
    dateRange[0] ? toISODate(dateRange[0]) : null,
    dateRange[1] ? toISODate(dateRange[1]) : null,
  ]
}
function fromISODateRange(isoDateRange: DateRangeISO): DateRange {
  if (!isoDateRange) return
  return [
    isoDateRange[0] ? fromISODate(isoDateRange[0]) : null,
    isoDateRange[1] ? fromISODate(isoDateRange[1]) : null,
  ]
}

function toTime24(timeAsDate: Date): string {
  return `${timeAsDate.getHours().toString().padStart(2, '0')}:${timeAsDate
    .getMinutes()
    .toString()
    .padStart(2, '0')}`
}
function fromTime24(time24: string): Date {
  const [hour, minute] = time24.split(':').map(Number)
  return new Date(2000, 0, 1, hour, minute)
}

/**
 * A composable function to handle date operations.
 *
 * @param {MaybeRefOrGetter<string>} datetime - The input date string. Can be either a date in ISO format or a full datetime format.
 *   Examples:
 *     - '2023-05-15'
 *     - '2023-05-15T14:30:00'
 * @param {function} setISODate - A function to set the new date value. Always receives a date in ISO format.
 *   Example: setISODate('2023-05-15')
 * @returns {Object} An object containing:
 *   - date: A computed Date object that can be get or set.
 *     Example: { date: { value: new Date('2023-05-15') } }
 *   - dateISO: A computed string representing the date in ISO format.
 *     Example: { dateISO: { value: '2023-05-15' } }
 */
export function useDate(
  datetime: MaybeRefOrGetter<string | null>,
  // eslint-disable-next-line no-unused-vars
  setISODate: (isoDate: string | null) => void,
) {
  const settingStore = useSettingStore()
  const date = computed<Date | null>({
    get: () => {
      const value = toValue(datetime)
      if (!value) return null
      return fromISODate(settingStore.dateIsoStr(value))
    },
    set: (date: Date | null) => {
      setISODate(date ? toISODate(date) : null)
    },
  })
  const dateISO = computed<string | null>(() => (date.value ? toISODate(date.value) : null))
  return { date, dateISO }
}

/**
 * A composable function to handle date range operations.
 *
 * @param {MaybeRefOrGetter<DateRangeISO>} datetimeRange - The input date range. Can be a ref, getter, or direct value of DateRangeISO type.
 *   It can contain ISO date ranges or full datetime values or null or a mix of both.
 *   Examples:
 *     - ['2023-05-15', '2023-05-20']
 *     - ['2023-05-15T14:30:00', '2023-05-20T09:00:00']
 *     - [null, null]
 *     - ['2023-05-15', null]
 *     - [null, '2023-05-20']
 *     - ['2023-05-15T14:30:00', null]
 *     - [null, '2023-05-20T09:00:00']
 * @param {function} setter - A function to set the new date range value. Always receives a range of ISO date strings or null.
 *   Examples:
 *     - setter(['2023-05-15', '2023-05-20'])
 *     - setter([null, null])
 *     - setter(['2023-05-15', null])
 *     - setter([null, '2023-05-20'])
 *     - setter(['2023-05-15T14:30:00', null])
 *     - setter([null, '2023-05-20T09:00:00'])
 * @returns {Object} An object containing:
 *   - dateRange: A computed DateRange object that can be get or set.
 *     Examples:
 *       - { value: [new Date('2023-05-15'), new Date('2023-05-20')] }
 *       - { value: [null, null] }
 *       - { value: [new Date('2023-05-15'), null] }
 *       - { value: [null, new Date('2023-05-20')] }
 *   - dateRangeISO: A computed DateRangeISO representing the date range in ISO format.
 *     Examples:
 *       - { value: ['2023-05-15', '2023-05-20'] }
 *       - { value: [null, null] }
 *       - { value: ['2023-05-15', null] }
 *       - { value: [null, '2023-05-20'] }
 */
export function useDateRange(
  datetimeRange: MaybeRefOrGetter<DateRangeISO>,
  // eslint-disable-next-line no-unused-vars
  setter: (value: DateRangeISO) => void,
) {
  const settingStore = useSettingStore()
  const dateRange = computed<DateRange>({
    get: () => {
      if (!toValue(datetimeRange)) return
      return fromISODateRange(
        toValue(datetimeRange)?.map(isoDate => (isoDate ? settingStore.dateIsoStr(isoDate) : null)),
      )
    },
    set: (dateRange: DateRange) => {
      setter(toISODateRange(dateRange))
    },
  })
  const dateRangeISO = computed<DateRangeISO>(() => toISODateRange(dateRange.value))
  return { dateRange, dateRangeISO }
}

/**
 * A composable function to handle time operations.
 *
 * @param {MaybeRefOrGetter<string>} datetime - The input datetime string. Can be a date in ISO format or the time in 24-hour format.
 *   Examples:
 *     - '2023-05-15T14:30:00'
 *     - '14:30'
 * @param {function} setter - A function to set the new time value. Always receives a time in 24-hour format.
 *   Examples:
 *     - setter('14:30')
 * @returns {Object} An object containing:
 *   - time: A computed Date object that can be get or set. It does take into account the current date, just the time.
 *     Examples:
 *       { value: new Date('2020-01-01T14:30:00') }
 *   - time24: A computed string representing the time in 24-hour format.
 *     Examples:
 *       { value: '14:30' }
 */
export function useTime(
  datetime: MaybeRefOrGetter<string | null>,
  // eslint-disable-next-line no-unused-vars
  setter: (time24: string | null) => void,
) {
  const settingStore = useSettingStore()
  const time = computed<Date | null>({
    get: () => {
      const value = toValue(datetime)
      if (!value) return null
      if (value.includes('T')) {
        return fromTime24(settingStore.time24Str(value))
      } else {
        return fromTime24(value)
      }
    },
    set: (timeInDate: Date | null) => {
      if (!timeInDate) return
      setter(toTime24(timeInDate))
    },
  })
  const time24 = computed<string | null>(() => {
    const value = toValue(datetime)
    if (!value) return null
    if (value.includes('T')) {
      return settingStore.time24Str(value)
    } else {
      return value
    }
  })
  return { time, time24 }
}

/**
 * Normalizes hours that may be >= 24 (representing next day) to 0-23 range
 * @param hours - Hours value (can be >= 24)
 * @returns Normalized hours in 0-23 range
 */
export function normalizeHours(hours: number): number {
  return hours >= 24 ? hours - 24 : hours
}

/**
 * Checks if an end time comes before or at the same time as start time (indicating overnight/next day)
 * @param startHours - Start time hours (0-23)
 * @param startMinutes - Start time minutes
 * @param endHours - End time hours (0-23)
 * @param endMinutes - End time minutes
 * @returns true if end time is before or equal to start time
 */
export function isEndTimeBeforeOrEqualStart(
  startHours: number,
  startMinutes: number,
  endHours: number,
  endMinutes: number,
): boolean {
  return endHours < startHours || (endHours === startHours && endMinutes <= startMinutes)
}

/**
 * Formats time with potential next-day hours (e.g., 27:00 for 3am next day)
 * @param hours - Hours value (0-23)
 * @param minutes - Minutes value
 * @param isNextDay - Whether this represents next day
 * @returns Time string in HH:mm format (hours may be >= 24 for next day)
 */
export function formatTimeWithNextDay(hours: number, minutes: number, isNextDay: boolean): string {
  const adjustedHours = isNextDay ? hours + 24 : hours
  return `${adjustedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

/**
 * Parses a time string that may have hours >= 24 (representing next day)
 * @param time24 - Time string in HH:mm format (hours may be >= 24)
 * @returns Object with normalized hours (0-23), minutes, and whether it's next day
 */
export function parseTimeWithNextDay(time24: string): {
  normalizedHours: number
  minutes: number
  isNextDay: boolean
} {
  const [hours, minutes] = time24.split(':').map(Number)
  const isNextDay = hours >= 24
  const normalizedHours = normalizeHours(hours)
  return { normalizedHours, minutes, isNextDay }
}

/**
 * Converts a time string to a Date object, handling hours >= 24
 * @param time24 - Time string in HH:mm format (hours may be >= 24)
 * @returns Date object with normalized hours
 */
export function time24ToDate(time24: string): Date {
  const { normalizedHours, minutes } = parseTimeWithNextDay(time24)
  return new Date(2000, 0, 1, normalizedHours, minutes)
}

/**
 * Adjusts end time relative to start time for overnight scenarios
 * When end time appears "before" start time in normalized form, it represents next day
 * @param startTime24 - Start time in HH:mm format
 * @param endTime24 - End time in HH:mm format
 * @returns Adjusted end time (may have hours >= 24 for next day)
 */
export function adjustEndTimeForOvernight(startTime24: string, endTime24: string): string {
  const [startHours, startMinutes] = startTime24.split(':').map(Number)
  const [endHours, endMinutes] = endTime24.split(':').map(Number)

  const normalizedStartHours = normalizeHours(startHours)
  const normalizedEndHours = normalizeHours(endHours)

  if (
    isEndTimeBeforeOrEqualStart(normalizedStartHours, startMinutes, normalizedEndHours, endMinutes)
  ) {
    return formatTimeWithNextDay(normalizedEndHours, endMinutes, true)
  }

  return formatTimeWithNextDay(normalizedEndHours, endMinutes, false)
}

/**
 * Calculates duration between start and end times in minutes
 * Handles overnight scenarios where end time may have hours >= 24
 * @param startTime24 - Start time in HH:mm format
 * @param endTime24 - End time in HH:mm format (may have hours >= 24)
 * @returns Duration in minutes
 */
export function calculateDurationMinutes(startTime24: string, endTime24: string): number {
  const [startHours, startMinutes] = startTime24.split(':').map(Number)
  const [endHours, endMinutes] = endTime24.split(':').map(Number)

  const startTotalMinutes = startHours * 60 + startMinutes
  const endTotalMinutes = endHours * 60 + endMinutes

  return endTotalMinutes - startTotalMinutes
}

/**
 * Converts a time string in 24-hour format to total minutes since midnight
 * Handles hours that may be >= 24 (representing next day)
 *
 * @param {string} time24 - Time string in HH:mm format (hours may be >= 24)
 * @returns {number} Total minutes since midnight
 * @example
 * hmToMinutes('14:30') // returns 870 (14 * 60 + 30)
 * hmToMinutes('25:00') // returns 1500 (25 * 60)
 * hmToMinutes('invalid') // returns 0
 */
export function hmToMinutes(time24: string): number {
  const [h, m] = time24.split(':').map(n => parseInt(n, 10))
  return (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m)
}

/**
 * Converts total minutes since midnight to a time string in 24-hour format
 * Can produce hours >= 24 to represent times in the next day
 *
 * @param {number} totalMinutes - Total minutes since midnight
 * @returns {string} Time string in HH:mm format (hours may be >= 24)
 * @example
 * minutesToHm(870) // returns '14:30'
 * minutesToHm(1500) // returns '25:00'
 * minutesToHm(90) // returns '1:30'
 */
export function minutesToHm(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${h}:${m.toString().padStart(2, '0')}`
}

/**
 * Formats a 24-hour time string into a 12-hour clock display format with AM/PM
 * Handles cases where hours may be >= 24 (representing next day)
 *
 * @param {string} time24 - Time string in HH:mm format (hours may be >= 24)
 * @returns {string} Formatted time string in 12-hour format with AM/PM
 * @example
 * formatDisplayTime12h('14:30') // returns '2:30 PM'
 * formatDisplayTime12h('25:00') // returns '1:00 AM' (next day)
 * formatDisplayTime12h('09:30') // returns '9:30 AM'
 */
export function formatDisplayTime12h(time24: string): string {
  const total = hmToMinutes(time24)
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  const baseHour = ((hours % 24) + 24) % 24
  const ampm = baseHour >= 12 ? 'PM' : 'AM'
  const displayHour = ((baseHour + 11) % 12) + 1
  const displayMinutes = `${minutes}`.padStart(2, '0')
  return `${displayHour}:${displayMinutes} ${ampm}`
}

/**
 * Formats a duration in minutes to a human-readable string
 * @param durationMinutes - Duration in minutes
 * @returns Formatted string like "2 hours 30 min" or "45 min" or "1 hour"
 */
export function formatDuration(durationMinutes: number): string {
  const hours = Math.floor(durationMinutes / 60)
  const minutes = durationMinutes % 60

  if (hours === 0) {
    return `${minutes} min`
  } else if (minutes === 0) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  } else {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} min`
  }
}

/**
 * A composable function to handle an end time that can span into the next day relative to a start time.
 * This is useful for overnight time ranges (e.g., 11:00 PM to 2:00 AM becomes 23:00 to 26:00).
 *
 * @param {MaybeRefOrGetter<string | null>} endTime - The end time string in 24-hour format (may have hours >= 24)
 * @param {MaybeRefOrGetter<string | null>} startTime - The start time string in 24-hour format for reference
 * @param {function} setter - A function to set the new end time value
 * @returns {Object} An object containing:
 *   - time: A computed Date object for the end time (normalized to 0-23 hours)
 *   - time24: A computed string of the end time (may have hours >= 24 for next day)
 */
export function useOvernightTime(
  endTime: MaybeRefOrGetter<string | null>,
  startTime: MaybeRefOrGetter<string | null>,
  // eslint-disable-next-line no-unused-vars
  setter: (time24: string | null) => void,
) {
  const time = computed<Date | null>({
    get: () => {
      const value = toValue(endTime)
      if (!value) return null
      return time24ToDate(value)
    },
    set: (timeInDate: Date | null) => {
      if (!timeInDate) return

      const hours = timeInDate.getHours()
      const minutes = timeInDate.getMinutes()
      const start = toValue(startTime)

      if (start) {
        const { normalizedHours: startHours, minutes: startMinutes } = parseTimeWithNextDay(start)
        const isNextDay = hours < startHours || (hours === startHours && minutes <= startMinutes)
        const adjustedHours = isNextDay ? hours + 24 : hours
        setter(
          `${adjustedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
        )
      } else {
        setter(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`)
      }
    },
  })

  const time24 = computed<string | null>(() => toValue(endTime))

  return { time, time24 }
}

/**
 * Normalizes a time string by ensuring proper HH:mm format with padded hours and minutes
 * Handles invalid or empty time strings
 *
 * @param {string} time24Like - Time string to normalize in HH:mm format
 * @returns {string} Normalized time string in HH:mm format or original string if empty
 * @example
 * normalizeTime('09:05:00') // returns '09:05'
 * normalizeTime('9:5') // returns '09:05'
 * normalizeTime('14:30') // returns '14:30'
 * normalizeTime('invalid') // returns '00:00'
 * normalizeTime('') // returns ''
 */
export function normalizeTime(time24Like: string): string {
  if (!time24Like) return time24Like
  const [hRaw = '0', mRaw = '0'] = time24Like.split(':')
  const hNum = parseInt(hRaw, 10)
  const mNum = parseInt(mRaw, 10)
  const h = Number.isNaN(hNum) ? 0 : hNum
  const m = Number.isNaN(mNum) ? 0 : mNum
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
}
